import React, { use } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsOffOutlined from '@mui/icons-material/NotificationsOffOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { DateTimePicker, renderDigitalClockTimeView } from '@mui/x-date-pickers';
import moment from 'moment';
import Constants from 'style/constants';
import SelectField from 'shared/components/formFields/select-field';
import { formatDateTimeWithoutYear } from 'shared/utils/date-utils';

import { MuteScenariosReason } from 'features/residents/models/mute-scenarios-reason';
import useMuteScenariosConfiguration from 'features/residents/resident-settings/components/resident-setting-details-mute-scenarios-accordion/mute-scenarios-configuration/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './mute-scenarios-configuration.module.scss';

const MuteScenariosConfiguration = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { residentSettingEditForm, viewingMode, currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  const { expanded, toggleExpand, handleReasonChange, validationRules } = useMuteScenariosConfiguration();

  const scenariosMuted = residentSettingEditForm.watch('scenariosMuted');
  const muteScenariosReason = residentSettingEditForm.watch('muteScenariosReason');
  const { errors } = useFormState({ control: residentSettingEditForm.control, name: 'muteScenariosReason' });
  const reasonError = errors.muteScenariosReason?.message as string | undefined;
  const visibleColor = scenariosMuted ? Constants.Colors.primary : Constants.Colors.inactive;
  const configurationRowContainerClassName = scenariosMuted
    ? `${styles.configurationRowContainer} ${styles.scenarioActive}`
    : styles.configurationRowContainer;

  const dateTimePickerLocaleText = {
    fieldYearPlaceholder: (): string => t('dateTimePickerLocale.fieldYearPlaceholder'),
    fieldDayPlaceholder: (): string => t('dateTimePickerLocale.fieldDayPlaceholder'),
    fieldMonthPlaceholder: (): string => t('dateTimePickerLocale.fieldMonthPlaceholder'),
    fieldHoursPlaceholder: (): string => t('dateTimePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: (): string => t('dateTimePickerLocale.fieldMinutesPlaceholder'),
  };

  const renderSwitch = (
    <Controller
      name="scenariosMuted"
      control={residentSettingEditForm.control}
      render={({ field }) => (
        <Switch
          className={styles.switch}
          type="checkbox"
          disabled={viewingMode === 'viewing'}
          checked={!!field.value}
          onChange={(_, checked) => field.onChange(checked)}
        />
      )}
    />
  );

  const reasonOptions = Object.values(MuteScenariosReason).map((reason) => (
    <MenuItem key={reason} value={reason}>
      {t(`muteScenarios.reasonOptions.${reason}`)}
    </MenuItem>
  ));

  return (
    <div className={styles.muteScenariosConfiguration}>
      <Accordion expanded={expanded} disableGutters>
        <AccordionSummary
          sx={{ padding: 0, margin: '8px 0 0 0', '.MuiAccordionSummary-content': { margin: '0' }, flexDirection: 'row-reverse' }}
          expandIcon={<ExpandMore onClick={() => toggleExpand()} />}
          aria-label="Expand"
          aria-controls="additional-actions1-content"
        >
          <div className={configurationRowContainerClassName}>
            {renderSwitch}
            <NotificationsOffOutlined sx={{ color: visibleColor }} />
            <Grid
              className={styles.configurationRowColumn}
              onClick={() => toggleExpand()}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && toggleExpand()}
            >
              <div className={styles.configurationRowInColumn}>
                <Typography variant="h5" className={`${styles.scenarioTitle} ${scenariosMuted ? '' : styles.scenarioTitleInactive}`}>
                  {t('muteScenarios.accordionTitle')}
                </Typography>
              </div>
              {scenariosMuted && currentResidentSetting?.muteScenariosUntil && (
                <div className={`${styles.configurationRowInColumn} ${styles.greyWhenInactive}`}>
                  <Typography variant="body2">
                    {t('muteScenarios.until')} {formatDateTimeWithoutYear(currentResidentSetting.muteScenariosUntil)}
                  </Typography>
                </div>
              )}
            </Grid>
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0 0 8px 0', margin: 0 }}>
          <div key={currentResidentSetting?.organisationUnitId} className={styles.detailsContainer}>
            <div className={styles.titleRow}>
              <Typography variant="subtitle1">{t('muteScenarios.accordionTitle')}</Typography>
            </div>
            <div className={styles.reasonRow}>
              <SelectField
                className={styles.reasonSelect}
                label={t('muteScenarios.reasonLabel')}
                disabled={viewingMode === 'viewing'}
                error={!!reasonError}
                helperText={reasonError}
                value={muteScenariosReason ?? ''}
                onChange={(event) => handleReasonChange(event.target.value as MuteScenariosReason)}
              >
                {reasonOptions}
              </SelectField>
            </div>
            <div className={styles.fromRow}>
              {muteScenariosReason && (
                <>
                  <Typography variant="subtitle1">
                    {t('muteScenarios.from')}{' '}
                    {currentResidentSetting?.muteScenariosFrom
                      ? formatDateTimeWithoutYear(currentResidentSetting.muteScenariosFrom)
                      : formatDateTimeWithoutYear(new Date().toISOString())}
                  </Typography>
                  <Controller
                    name="muteScenariosUntil"
                    control={residentSettingEditForm.control}
                    rules={validationRules.until}
                    render={({ field, fieldState }) => (
                      <DateTimePicker
                        className={styles.muteScenariosUntilPicker}
                        ampm={false}
                        minDateTime={moment().add(5, 'minutes')}
                        maxDateTime={moment().add(1, 'month')}
                        label={t('muteScenarios.until')}
                        closeOnSelect={false}
                        format="DD-MM HH:mm"
                        localeText={dateTimePickerLocaleText}
                        value={field.value && typeof field.value === 'string' ? moment(field.value) : null}
                        onChange={(value) => field.onChange(value?.toISOString() ?? null)}
                        disabled={viewingMode === 'viewing'}
                        views={['hours', 'day']}
                        viewRenderers={{
                          hours: renderDigitalClockTimeView,
                        }}
                        slotProps={{
                          textField: {
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </>
              )}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default MuteScenariosConfiguration;
